/**
 * Kitchen hours utilities — TypeScript port of src/utils/kitchenHours.js
 * (the tablet uses the JS file). Behaviour kept strictly identical so the
 * tablet and PWA agree on whether the kitchen is open.
 *
 * Données (Restaurant.settings) :
 *  - kitchenHours: Array<{ openTime: "HH:MM", closeTime: "HH:MM" }>  → horaires par défaut (vide = toujours ouvert)
 *  - kitchenHoursOverrides: { [dayKey]: { periods: [...] } | { closed: true } }
 *      dayKey = "0" (dimanche) … "6" (samedi) ou "publicHoliday".
 *      Priorité : jour férié > jour de semaine > horaires par défaut.
 *
 * Les helpers acceptent soit un tableau de périodes, soit `{ closed: true }`
 * (cuisine fermée toute la journée).
 */
import { isPublicHolidayToday } from "./publicHolidays";

export interface KitchenHour {
  openTime?: string; // "HH:MM"
  closeTime?: string; // "HH:MM"
}

/** Cuisine fermée toute la journée (jour personnalisé « fermé »). */
export interface KitchenClosedAllDay {
  closed: true;
}

export type KitchenHours = KitchenHour[] | KitchenClosedAllDay;

export interface KitchenDayOverride {
  periods?: KitchenHour[];
  closed?: boolean;
}

/** Sous-ensemble de Restaurant.settings utile au calcul des horaires cuisine. */
export interface KitchenHoursSettings {
  kitchenHours?: KitchenHour[] | null;
  kitchenHoursOverrides?: Record<string, KitchenDayOverride> | null;
  buffetSettings?: { kitchenHours?: KitchenHour[] } | null;
}

type MaybeKitchenHours = KitchenHours | null | undefined;

const timeToMinutes = (time?: string): number => {
  if (!time || typeof time !== "string") return 0;
  const [h, m] = time.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const isClosedAllDay = (hours: MaybeKitchenHours): boolean =>
  !!hours && !Array.isArray(hours) && hours.closed === true;

const toPeriods = (hours: MaybeKitchenHours): KitchenHour[] => (Array.isArray(hours) ? hours : []);

/**
 * Combien de temps avant l'annonce « dernière commande à HH:MM » : le message
 * n'apparaît qu'une fois dans cette fenêtre, pour ne pas afficher une échéance
 * encore lointaine (et donc inutile) pendant tout le service.
 */
export const DEADLINE_NOTICE_MINUTES = 45;

/** Minutes restantes avant `closeTime`, en gérant une fermeture après minuit. */
const minutesUntilClosing = (closeTime: string, now: Date = new Date()): number => {
  const current = now.getHours() * 60 + now.getMinutes();
  const diff = timeToMinutes(closeTime) - current;
  return diff >= 0 ? diff : diff + 24 * 60;
};

/**
 * Clé du jour courant au format des overrides ("0"…"6" ou "publicHoliday").
 * `publicHoliday` peut être forcé (sinon calculé sur les listes de jours fériés).
 */
export const getKitchenDayKey = (now: Date = new Date(), publicHoliday?: boolean): string => {
  const holiday = typeof publicHoliday === "boolean" ? publicHoliday : isPublicHolidayToday(now);
  return holiday ? "publicHoliday" : String(now.getDay());
};

/**
 * Horaires cuisine effectifs pour aujourd'hui : override du jour si présent,
 * sinon horaires par défaut. Un jour personnalisé remplace entièrement le défaut.
 */
export const getEffectiveKitchenHours = (
  settings?: KitchenHoursSettings | null,
  opts: { now?: Date; publicHoliday?: boolean } = {},
): KitchenHours => {
  const base = settings?.kitchenHours || settings?.buffetSettings?.kitchenHours || [];
  const overrides = settings?.kitchenHoursOverrides;
  if (!overrides || typeof overrides !== "object") return base;
  // Priorité : jour férié > jour de semaine > horaires par défaut.
  const weekdayKey = String((opts.now ?? new Date()).getDay());
  const override = overrides[getKitchenDayKey(opts.now, opts.publicHoliday)] ?? overrides[weekdayKey];
  if (!override) return base;
  if (override.closed) return { closed: true };
  return Array.isArray(override.periods) ? override.periods : base;
};

/** True when there are no kitchen hours OR the current time falls inside any open period. */
export const isKitchenOpen = (kitchenHours?: MaybeKitchenHours): boolean => {
  if (isClosedAllDay(kitchenHours)) return false;
  const periods = toPeriods(kitchenHours);
  if (periods.length === 0) return true;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  return periods.some((period) => {
    if (!period.openTime || !period.closeTime) return false;
    const open = timeToMinutes(period.openTime);
    const close = timeToMinutes(period.closeTime);
    if (close > open) return cur >= open && cur < close;
    // midnight crossing
    return cur >= open || cur < close;
  });
};

export const getClosingTime = (kitchenHours?: MaybeKitchenHours): string | null => {
  if (isClosedAllDay(kitchenHours)) return null;
  const periods = toPeriods(kitchenHours);
  if (periods.length === 0) return null;
  const cur = new Date().getHours() * 60 + new Date().getMinutes();
  for (const p of periods) {
    if (!p.openTime || !p.closeTime) continue;
    const open = timeToMinutes(p.openTime);
    const close = timeToMinutes(p.closeTime);
    if (close > open) {
      if (cur >= open && cur < close) return p.closeTime;
    } else {
      if (cur >= open || cur < close) return p.closeTime;
    }
  }
  return null;
};

/**
 * Fin du dernier service terminé aujourd'hui ("HH:MM"), ou null si aucun service
 * ne s'est encore terminé. Gère les services à cheval sur minuit.
 */
export const getLastClosingTime = (kitchenHours?: MaybeKitchenHours): string | null => {
  if (isClosedAllDay(kitchenHours)) return null;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();

  let best: string | null = null;
  let bestElapsed = Infinity;
  for (const p of toPeriods(kitchenHours)) {
    if (!p.openTime || !p.closeTime) continue;
    const open = timeToMinutes(p.openTime);
    const close = timeToMinutes(p.closeTime);
    const ended = close > open ? cur >= close : cur >= close && cur < open;
    if (!ended) continue;
    const elapsed = (cur - close + 24 * 60) % (24 * 60);
    if (elapsed < bestElapsed) {
      bestElapsed = elapsed;
      best = p.closeTime;
    }
  }
  return best;
};

/**
 * Returns the next openTime > now, else wraps to the earliest openTime
 * in the list (next day's first slot).
 *
 * N.B. volontairement plus utilisé dans les messages client : l'heure retournée
 * est ambiguë dès qu'elle est celle du lendemain (voire d'un jour plus tard si
 * le jour suivant est personnalisé ou fermé). Conservé pour l'outillage/les tests.
 */
export const getNextOpeningTime = (kitchenHours?: MaybeKitchenHours): string | null => {
  if (isClosedAllDay(kitchenHours)) return null;
  const valid = toPeriods(kitchenHours).filter(
    (p): p is Required<KitchenHour> => !!p.openTime && !!p.closeTime,
  );
  if (!valid.length) return null;
  const cur = new Date().getHours() * 60 + new Date().getMinutes();
  const upcoming = valid
    .map((p) => ({ openTime: p.openTime, openMinutes: timeToMinutes(p.openTime) }))
    .filter((p) => p.openMinutes > cur)
    .sort((a, b) => a.openMinutes - b.openMinutes);
  if (upcoming.length) return upcoming[0]!.openTime;
  return [...valid]
    .sort((a, b) => timeToMinutes(a.openTime) - timeToMinutes(b.openTime))[0]!.openTime;
};

export const formatKitchenHours = (kitchenHours?: MaybeKitchenHours): string => {
  if (isClosedAllDay(kitchenHours)) return "";
  return toPeriods(kitchenHours)
    .filter((p) => p.openTime && p.closeTime)
    .map((p) => `${p.openTime} - ${p.closeTime}`)
    .join(", ");
};

/** Format a remaining duration in ms as "X min Y s" or "Y s". */
/**
 * État de la cuisine, sous une forme prête à afficher.
 *
 * Contrairement au portage de la tablette, la traduction n'est pas faite ici :
 * la fonction renvoie de la donnée (`kind` + `time`), et l'appelant choisit sa
 * clé i18n. Ça évite de passer `t` dans une couche `lib/` qui doit rester pure
 * et testable.
 *
 * `closingTime` = heure de fermeture du service en cours (null si fermé).
 * `showDeadline` = vrai uniquement dans les DEADLINE_NOTICE_MINUTES qui
 * précèdent cette fermeture : c'est la condition d'affichage du « dernière
 * commande à HH:MM ».
 */
export interface KitchenStatus {
  isOpen: boolean;
  /** Quel message afficher : rien, l'échéance, ou l'indisponibilité. */
  kind: "open" | "deadline" | "unavailable" | "unavailableSince";
  /** Heure associée au message (échéance ou fin du dernier service). */
  time: string | null;
  closingTime: string | null;
  showDeadline: boolean;
}

// Pas de paramètre `now` : isKitchenOpen / getClosingTime / getLastClosingTime
// lisent l'horloge directement (comme le portage JS de référence). En accepter
// un ici laisserait croire que l'heure est injectable alors que seule une
// partie du calcul l'utiliserait. Les tests figent le temps avec
// vi.setSystemTime.
export const getKitchenStatus = (kitchenHours?: MaybeKitchenHours): KitchenStatus => {
  const open = isKitchenOpen(kitchenHours);
  const closingTime = getClosingTime(kitchenHours);

  if (open) {
    const showDeadline =
      !!closingTime && minutesUntilClosing(closingTime) <= DEADLINE_NOTICE_MINUTES;
    return {
      isOpen: true,
      kind: showDeadline ? "deadline" : "open",
      time: showDeadline ? closingTime : null,
      closingTime,
      showDeadline,
    };
  }

  // Hors service : pas d'heure de réouverture. Une période ne porte que
  // l'heure, jamais le jour, donc la prochaine ouverture peut tomber le
  // lendemain (ou plus tard) et induirait le client en erreur.
  const lastClosing = getLastClosingTime(kitchenHours);
  return {
    isOpen: false,
    kind: lastClosing ? "unavailableSince" : "unavailable",
    time: lastClosing,
    closingTime: null,
    showDeadline: false,
  };
};

export const formatRemaining = (ms: number): string => {
  if (ms <= 0) return "0 s";
  const totalSec = Math.ceil(ms / 1000);
  if (totalSec < 60) return `${totalSec} s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec - min * 60;
  return sec > 0 ? `${min} min ${sec} s` : `${min} min`;
};
